import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * Displays a promotional dialog after a configurable delay and remembers a
 * dismissal in a first-party cookie.
 */
class LandingPopup extends DialogComponent {
  #showTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(DialogCloseEvent.eventName, this.#rememberDismissal);
    this.querySelector('[data-landing-popup-link]')?.addEventListener('click', this.#rememberDismissal);
    document.addEventListener('shopify:section:select', this.#handleSectionSelect);

    const shouldShow = this.#isThemeEditor || this.dataset.frequencyUnit === 'visit' || !this.#hasDismissalCookie;

    if (this.#isEnabled && shouldShow) {
      this.#schedulePopup();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#showTimer);
    this.removeEventListener(DialogCloseEvent.eventName, this.#rememberDismissal);
    this.querySelector('[data-landing-popup-link]')?.removeEventListener('click', this.#rememberDismissal);
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
  }

  #schedulePopup() {
    window.clearTimeout(this.#showTimer);

    const delay = Math.max(0, Number(this.dataset.delaySeconds) || 0) * 1000;
    this.#showTimer = window.setTimeout(() => this.showDialog(), this.#isThemeEditor ? 0 : delay);
  }

  #handleSectionSelect = (event) => {
    if (!this.#isEnabled || event.detail?.sectionId !== this.dataset.sectionId) return;
    this.showDialog();
  };

  #rememberDismissal = () => {
    if (this.#isThemeEditor || this.dataset.frequencyUnit === 'visit') return;

    const cookieParts = [`${this.#cookieName}=1`, 'Path=/', 'SameSite=Lax'];
    const maxAge = this.#cookieMaxAge;

    if (maxAge !== null) cookieParts.push(`Max-Age=${maxAge}`);
    if (window.location.protocol === 'https:') cookieParts.push('Secure');

    document.cookie = cookieParts.join('; ');
  };

  get #hasDismissalCookie() {
    return document.cookie.split('; ').some((cookie) => cookie.startsWith(`${this.#cookieName}=`));
  }

  get #cookieName() {
    const { frequencyUnit = 'session', frequencyInterval = '1' } = this.dataset;

    return `landing_popup_${this.dataset.sectionId || 'default'}_${frequencyUnit}_${frequencyInterval}`.replace(
      /[^a-zA-Z0-9_-]/g,
      '_'
    );
  }

  get #cookieMaxAge() {
    const interval = Math.max(1, Number(this.dataset.frequencyInterval) || 1);

    switch (this.dataset.frequencyUnit) {
      case 'hours':
        return interval * 60 * 60;
      case 'days':
        return interval * 24 * 60 * 60;
      case 'weeks':
        return interval * 7 * 24 * 60 * 60;
      case 'once':
        return 10 * 365 * 24 * 60 * 60;
      case 'session':
      default:
        return null;
    }
  }

  get #isThemeEditor() {
    return this.dataset.designMode === 'true';
  }

  get #isEnabled() {
    return this.dataset.enabled === 'true';
  }
}

if (!customElements.get('landing-popup')) customElements.define('landing-popup', LandingPopup);
